import React from 'react';
import { cn, Icon } from '@lqa-frontend/ui-system';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

type RadixCheckboxProps = React.ComponentProps<typeof CheckboxPrimitive.Root>;

export interface ICheckboxProps extends Omit<
	RadixCheckboxProps,
	'checked' | 'onCheckedChange' | 'onChange'
> {
	isChecked?: boolean | 'indeterminate';
	isIndeterminate?: boolean;
	onCheckedChange?: (checked: boolean | 'indeterminate') => void;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function Checkbox({
	className,
	isChecked: isCheckedProp,
	isIndeterminate,
	onChange,
	onCheckedChange,
	...props
}: ICheckboxProps) {
	const checked: boolean | 'indeterminate' =
		isIndeterminate === true ? 'indeterminate' : (isCheckedProp ?? false);
	const handleCheckedChange = (value: boolean | 'indeterminate') => {
		onCheckedChange?.(value);
		if (onChange) {
			const syntheticEvent = {
				target: { checked: value === true },
			} as React.ChangeEvent<HTMLInputElement>;
			onChange(syntheticEvent);
		}
	};

	return (
		<CheckboxPrimitive.Root
			data-slot="checkbox"
			checked={checked}
			onCheckedChange={handleCheckedChange}
			className={cn(
				'group peer inline-flex size-16 shrink-0 cursor-pointer items-center justify-center rounded-xs border transition-colors outline-none',
				'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-outline-interactive',
				// unchecked
				'border-divider bg-base-1',
				'data-[state=unchecked]:hover:border-brand-blue-500',
				// checked / indeterminate
				'data-[state=checked]:border-brand-blue-600 data-[state=checked]:bg-brand-blue-600',
				'data-[state=indeterminate]:border-brand-blue-600 data-[state=indeterminate]:bg-brand-blue-600',
				// checked/indeterminate + hover
				'data-[state=checked]:hover:border-brand-blue-500 data-[state=checked]:hover:bg-brand-blue-500',
				'data-[state=indeterminate]:hover:border-brand-blue-500 data-[state=indeterminate]:hover:bg-brand-blue-500',
				// disabled unchecked
				'disabled:data-[state=unchecked]:cursor-not-allowed disabled:data-[state=unchecked]:border-zinc-500 disabled:data-[state=unchecked]:bg-zinc-200',
				// disabled checked/indeterminate
				'disabled:data-[state=checked]:cursor-not-allowed disabled:data-[state=checked]:border-zinc-300 disabled:data-[state=checked]:bg-zinc-300',
				'disabled:data-[state=indeterminate]:cursor-not-allowed disabled:data-[state=indeterminate]:border-zinc-300 disabled:data-[state=indeterminate]:bg-zinc-300',
				className
			)}
			{...props}
		>
			<CheckboxPrimitive.Indicator
				data-slot="checkbox-indicator"
				className="flex items-center justify-center text-current transition-colors"
			>
				{checked === 'indeterminate' ? (
					<Icon name="minus" className="text-white" />
				) : (
					<Icon
						name="checkLg"
						size="xs"
						className="transition-colors text-white"
					/>
				)}
			</CheckboxPrimitive.Indicator>
		</CheckboxPrimitive.Root>
	);
}

export { Checkbox };
